import type {
  CompanyDetail,
  CompanySummary,
  ProductSummary,
  RepresentativeSummary,
  ServiceSummary,
} from "@mvk/shared";
import {
  resolveBadgeQrCode,
  resolveBoothDisplayQrCode,
  resolveProductQrCode,
} from "@mvk/shared";
import type { Company, Product, Representative, Service } from "../types";
import { isApiEnabled } from "../api/config";
import { visitorAuthStorage, adminAuthStorage } from "../api/authStorage";
import { catalogApi } from "../api/catalog";
import { eventPackService } from "./eventPackService";
import { engagementService } from "./engagementService";
import { storage, KEYS } from "./storage";
import { printableQr } from "../utils/publicQr";

export function mapProductSummaryToProduct(summary: ProductSummary): Product {
  return {
    id: summary.id,
    companyId: summary.companyId,
    eventId: summary.eventId,
    name: summary.name,
    description: summary.description,
    category: summary.category,
    images: summary.images ?? [],
    tags: summary.tags ?? [],
    qrCode: printableQr(resolveProductQrCode(summary.id, summary.qrCode)),
    barcode: summary.barcode,
    price: summary.price,
  };
}

export function mapRepresentativeSummaryToRepresentative(
  summary: RepresentativeSummary,
): Representative {
  return {
    id: summary.id,
    companyId: summary.companyId,
    eventId: summary.eventId,
    fullName: summary.fullName,
    position: summary.position,
    photo: summary.photoUrl,
    bio: summary.bio,
    phone: summary.phone,
    publicPhone: summary.publicPhone,
    email: summary.email,
    telegram: summary.telegram,
    badgeQrCode: printableQr(resolveBadgeQrCode(summary.id, summary.badgeQrCode)),
    badgeBarcode: summary.badgeBarcode?.trim() || undefined,
  };
}

export function mapServiceSummaryToService(summary: ServiceSummary): Service {
  return {
    id: summary.id,
    companyId: summary.companyId,
    eventId: summary.eventId,
    name: summary.name,
    description: summary.description,
    category: summary.category,
    tags: summary.tags ?? [],
    price: summary.price,
  };
}

function mapSummaryToCompany(summary: CompanySummary, eventId: string): Company {
  return {
    id: summary.id,
    eventId,
    name: summary.name,
    category: summary.category,
    tags: [],
    hall: summary.hall ?? "",
    booth: summary.booth ?? "",
    shortAbout: summary.shortAbout,
    products: [],
    contact: {},
    qrCodeValue: `company:${summary.id}`,
    mapPoint: { x: 0, y: 0, hallId: "" },
  };
}

function mapDetailToCompany(detail: CompanyDetail): Company {
  return {
    id: detail.id,
    eventId: detail.eventId,
    name: detail.name,
    category: detail.category,
    tags: [],
    hall: detail.hall ?? "",
    booth: detail.booth ?? "",
    shortAbout: detail.shortAbout,
    products: [],
    website: detail.website,
    contact: { phone: detail.phone, email: detail.email },
    logoUrl: detail.logoUrl,
    qrCodeValue: resolveBoothDisplayQrCode(detail),
    boothQrCode: detail.boothQrCode,
    mapPoint: { x: 0, y: 0, hallId: "" },
  };
}

function includesQuery(haystack: string | undefined | null, q: string): boolean {
  return (haystack ?? "").toLowerCase().includes(q);
}

async function searchLocalFromPack(eventId: string, query: string): Promise<CatalogSearchResult> {
  const pack = await eventPackService.getCached(eventId);
  if (!pack) {
    return { companies: [], products: [], services: [], representatives: [] };
  }

  const q = query.toLowerCase();
  const companyNameById = new Map(pack.companies.map((c) => [c.id, c.name]));

  const companies = pack.companies
    .filter(
      (c) =>
        includesQuery(c.name, q) ||
        includesQuery(c.category, q) ||
        includesQuery(c.shortAbout, q) ||
        includesQuery(c.booth, q) ||
        includesQuery(c.hall, q),
    )
    .map(mapDetailToCompany);

  const products = pack.products
    .filter(
      (p) =>
        includesQuery(p.name, q) ||
        includesQuery(p.description, q) ||
        includesQuery(p.category, q) ||
        (p.tags ?? []).some((t) => includesQuery(t, q)),
    )
    .map((p) => ({
      ...mapProductSummaryToProduct(p),
      companyName: companyNameById.get(p.companyId),
    }));

  const representatives = pack.representatives
    .filter(
      (r) =>
        includesQuery(r.fullName, q) ||
        includesQuery(r.position, q) ||
        includesQuery(r.bio, q),
    )
    .map((r) => ({
      ...mapRepresentativeSummaryToRepresentative(r),
      companyName: companyNameById.get(r.companyId),
    }));

  return { companies, products, services: [], representatives };
}

function readLocalRecent(eventId: string): string[] {
  const map = storage.get<Record<string, string[]>>(KEYS.SEARCH_RECENT_BY_EVENT, {});
  return Array.isArray(map[eventId]) ? map[eventId]! : [];
}

function pushLocalRecent(eventId: string, query: string): string[] {
  const q = query.trim();
  if (!q) return readLocalRecent(eventId);
  const map = storage.get<Record<string, string[]>>(KEYS.SEARCH_RECENT_BY_EVENT, {});
  const prev = Array.isArray(map[eventId]) ? map[eventId]! : [];
  const next = [q, ...prev.filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, 12);
  map[eventId] = next;
  storage.set(KEYS.SEARCH_RECENT_BY_EVENT, map);
  return next;
}

export interface CatalogSearchResult {
  companies: Company[];
  products: Array<Product & { companyName?: string }>;
  services: Array<Service & { companyName?: string }>;
  representatives: Array<Representative & { companyName?: string }>;
}

function canUseApi(audience: "visitor" | "admin"): boolean {
  return (
    isApiEnabled() &&
    (audience === "admin"
      ? adminAuthStorage.isAuthenticated()
      : visitorAuthStorage.isAuthenticated())
  );
}

function isProbablyOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export const catalogService = {
  async search(eventId: string, query: string): Promise<CatalogSearchResult> {
    const q = query.trim();
    if (!q) {
      return { companies: [], products: [], services: [], representatives: [] };
    }

    const online = canUseApi("visitor") && !isProbablyOffline();
    if (online) {
      try {
        const data = await catalogApi.search(eventId, q);
        return {
          companies: data.companies.map((c) => mapSummaryToCompany(c, eventId)),
          products: data.products.map((p) => ({
            ...mapProductSummaryToProduct(p),
            companyName: p.companyName,
          })),
          services: data.services.map((s) => ({
            ...mapServiceSummaryToService(s),
            companyName: s.companyName,
          })),
          representatives: data.representatives.map((r) => ({
            ...mapRepresentativeSummaryToRepresentative(r),
            companyName: r.companyName,
          })),
        };
      } catch {
        // Fall through to local pack search.
      }
    }

    return searchLocalFromPack(eventId, q);
  },

  async getSearchSuggestions(eventId: string): Promise<{ recent: string[]; popular: string[] }> {
    const localRecent = readLocalRecent(eventId);
    if (!canUseApi("visitor") || isProbablyOffline()) {
      return { recent: localRecent, popular: [] };
    }
    try {
      const data = await catalogApi.getSearchSuggestions(eventId);
      return {
        recent: data.recent.length ? data.recent : localRecent,
        popular: data.popular,
      };
    } catch {
      return { recent: localRecent, popular: [] };
    }
  },

  async recordSearchQuery(
    eventId: string,
    query: string,
  ): Promise<{ recent: string[]; popular: string[] }> {
    void engagementService.trackSearch(eventId, query);
    const localRecent = pushLocalRecent(eventId, query);
    if (!canUseApi("visitor") || isProbablyOffline()) {
      return { recent: localRecent, popular: [] };
    }
    try {
      const data = await catalogApi.recordSearchQuery(eventId, query);
      return {
        recent: data.recent.length ? data.recent : localRecent,
        popular: data.popular,
      };
    } catch {
      return { recent: localRecent, popular: [] };
    }
  },

  async clearRecentSearches(eventId: string): Promise<void> {
    const map = storage.get<Record<string, string[]>>(KEYS.SEARCH_RECENT_BY_EVENT, {});
    delete map[eventId];
    storage.set(KEYS.SEARCH_RECENT_BY_EVENT, map);
    if (!canUseApi("visitor") || isProbablyOffline()) return;
    try {
      await catalogApi.clearRecentSearches(eventId);
    } catch {
      // ignore
    }
  },

  async listProducts(
    eventId: string,
    companyId: string,
    audience: "visitor" | "admin" = "visitor",
  ): Promise<Product[]> {
    if (audience === "visitor") {
      // Prefer offline pack — never N+1 live API when pack already has data.
      const fromPack = await eventPackService.listProducts(eventId, companyId);
      if (fromPack.length) return fromPack.map(mapProductSummaryToProduct);
      const pack = await eventPackService.getCached(eventId);
      if (pack) return [];
    }
    if (!canUseApi(audience)) throw new Error("[catalogService] API auth required");
    const data = await catalogApi.listProducts(eventId, companyId, audience);
    return data.products.map(mapProductSummaryToProduct);
  },

  async listRepresentatives(
    eventId: string,
    companyId: string,
    audience: "visitor" | "admin" = "visitor",
  ): Promise<Representative[]> {
    if (audience === "visitor") {
      const fromPack = await eventPackService.listRepresentatives(eventId, companyId);
      if (fromPack.length) return fromPack.map(mapRepresentativeSummaryToRepresentative);
      const pack = await eventPackService.getCached(eventId);
      if (pack) return [];
    }
    if (!canUseApi(audience)) throw new Error("[catalogService] API auth required");
    const data = await catalogApi.listRepresentatives(eventId, companyId, audience);
    return data.representatives.map(mapRepresentativeSummaryToRepresentative);
  },

  /** O(1) pack lookup by representative id — avoids scanning every company. */
  async findRepresentative(
    eventId: string,
    representativeId: string,
  ): Promise<Representative | undefined> {
    const fromPack = await eventPackService.listRepresentatives(eventId);
    const hit = fromPack.find((r) => r.id === representativeId);
    if (hit) return mapRepresentativeSummaryToRepresentative(hit);
    return undefined;
  },

  async createProduct(
    eventId: string,
    companyId: string,
    input: {
      name: string;
      description?: string;
      category?: string;
      tags?: string[];
      price?: string;
      images?: string[];
    },
  ): Promise<Product> {
    if (!canUseApi("admin")) throw new Error("[catalogService] admin API required");
    const data = await catalogApi.createProduct(eventId, companyId, input);
    return mapProductSummaryToProduct(data.product);
  },

  async updateProduct(
    eventId: string,
    companyId: string,
    productId: string,
    input: {
      name?: string;
      description?: string;
      category?: string;
      tags?: string[];
      price?: string;
      images?: string[];
    },
  ): Promise<Product> {
    if (!canUseApi("admin")) throw new Error("[catalogService] admin API required");
    const data = await catalogApi.updateProduct(eventId, companyId, productId, input);
    return mapProductSummaryToProduct(data.product);
  },

  async deleteProduct(eventId: string, companyId: string, productId: string): Promise<void> {
    if (!canUseApi("admin")) throw new Error("[catalogService] admin API required");
    await catalogApi.deleteProduct(eventId, companyId, productId);
  },

  async createRepresentative(
    eventId: string,
    companyId: string,
    input: {
      fullName: string;
      position?: string;
      bio?: string;
      phone?: string;
      email?: string;
      telegram?: string;
    },
  ): Promise<Representative> {
    if (!canUseApi("admin")) throw new Error("[catalogService] admin API required");
    const data = await catalogApi.createRepresentative(eventId, companyId, input);
    return mapRepresentativeSummaryToRepresentative(data.representative);
  },

  async updateRepresentative(
    eventId: string,
    companyId: string,
    repId: string,
    input: {
      fullName?: string;
      position?: string;
      bio?: string;
      phone?: string;
      email?: string;
      telegram?: string;
    },
  ): Promise<Representative> {
    if (!canUseApi("admin")) throw new Error("[catalogService] admin API required");
    const data = await catalogApi.updateRepresentative(eventId, companyId, repId, input);
    return mapRepresentativeSummaryToRepresentative(data.representative);
  },

  async deleteRepresentative(eventId: string, companyId: string, repId: string): Promise<void> {
    if (!canUseApi("admin")) throw new Error("[catalogService] admin API required");
    await catalogApi.deleteRepresentative(eventId, companyId, repId);
  },

  async listServices(
    eventId: string,
    companyId: string,
    audience: "visitor" | "admin" = "visitor",
  ): Promise<Service[]> {
    if (!canUseApi(audience)) throw new Error("[catalogService] API auth required");
    const data = await catalogApi.listServices(eventId, companyId, audience);
    return data.services.map(mapServiceSummaryToService);
  },

  async createService(
    eventId: string,
    companyId: string,
    input: { name: string; description?: string; category?: string; tags?: string[]; price?: string },
  ): Promise<Service> {
    if (!canUseApi("admin")) throw new Error("[catalogService] admin API required");
    const data = await catalogApi.createService(eventId, companyId, input);
    return mapServiceSummaryToService(data.service);
  },

  async updateService(
    eventId: string,
    companyId: string,
    serviceId: string,
    input: { name?: string; description?: string; category?: string; tags?: string[]; price?: string },
  ): Promise<Service> {
    if (!canUseApi("admin")) throw new Error("[catalogService] admin API required");
    const data = await catalogApi.updateService(eventId, companyId, serviceId, input);
    return mapServiceSummaryToService(data.service);
  },

  async deleteService(eventId: string, companyId: string, serviceId: string): Promise<void> {
    if (!canUseApi("admin")) throw new Error("[catalogService] admin API required");
    await catalogApi.deleteService(eventId, companyId, serviceId);
  },
};
